import { fmtMoneda } from '../../utils/moneda';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, insertRow } from '../../db/engine';
import { etiquetaEstadoPoliza } from '../../db/renovaciones';
import { useApp } from '../../context/AppContext';
import { X, CreditCard, FileText, AlertTriangle, Printer, Check, Eye, Send, Search } from 'lucide-react';
import TabRemesas from './TabRemesas';
import ClienteSelect from '../../components/ClienteSelect';
import DetalleCobrosModal from '../../components/DetalleCobrosModal';
import { reaplicarSacoPoliza } from '../../db/saco';
import { fmtFecha } from '../../utils/fecha';
import { etiquetaDondePago } from '../../utils/reciboCobro';
import PolizaSelect from '../../components/PolizaSelect';
import { marcaImpresion } from '../../utils/marca';
import { imprimirHTML } from '../../utils/imprimir';
import { loadXLSX } from '../../utils/xlsx';

// Re-export para mantener compatibilidad con módulos que importan
// reaplicarSacoPoliza desde este archivo (Polizas, ImportarEstadoCuenta).
export { reaplicarSacoPoliza };

// Las operaciones MASIVAS sobre `pagos` (generar planes de cobro en lote, corregir
// saldos) se hacen por SQL en Supabase, no por botones en la app: las consultas del
// frontend se cortan en ~1000 filas y un lote truncado pisa montos y estados.
// Ver la regla en CLAUDE.md.

// reaplicarSacoPoliza se movió a src/db/saco.js (módulo compartido sin dependencias
// de componentes, para que DeclaracionesPanel pueda usarlo sin import circular).
// Se reexporta arriba para no romper los importadores existentes.

const fmt$ = fmtMoneda;
const hoy = () => new Date().toISOString().slice(0,10);
// Último día del mes en curso (YYYY-MM-DD). El cliente paga al inicio de cada periodo,
// así que lo "por cobrar este mes" incluye lo vencido + la cuota cuyo periodo cae en este mes.
const finMes = () => {
  const d = new Date();
  const y = d.getFullYear(), m = d.getMonth();
  const ultimo = new Date(y, m + 1, 0).getDate();
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
};




const EMPTY_PAGO_FORM = {
  poliza_id:'', monto:'', estado:'pendiente',
  fecha_vencimiento:'', metodo:'', referencia:'', notas:'',
};


// ──────────────────────────────────────────────────────────────
// TAB: PAGOS
// ──────────────────────────────────────────────────────────────
function TabPagos() {
  const { agenteScope, esAgenteRestringido } = useApp();
  const [rows, setRows]           = useState([]);
  const [sortCol, setSortCol]     = useState('');   // columna de orden (clic en encabezado)
  const [sortDir, setSortDir]     = useState('asc'); // 'asc' | 'desc'
  const [polizas, setPolizas]     = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [filtroPoliza, setFiltroPoliza] = useState('');
  const [filterEstado, setFilterEstado] = useState('pendiente');
  const [filterAseg, setFilterAseg]     = useState('');
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_PAGO_FORM);
  const navigate = useNavigate();
  // Abre la vista COMPLETA de la póliza navegando al módulo Pólizas (misma vista que "Ver póliza").
  const verPolizaCompleta = (polizaId) => {
    if (!polizaId) return;
    sessionStorage.setItem('openPolizaId', String(polizaId));
    sessionStorage.setItem('backFrom', 'cobros');
    navigate('/polizas');
  };
  // Póliza cuyo detalle de cobros está abierto (ventana compartida DetalleCobrosModal)
  const [detalleRef, setDetalleRef] = useState(null);  // { poliza_id, poliza_num, cliente }
  const [filtroPago, setFiltroPago] = useState('pendiente'); // 'pendiente' | 'pagado' | ''
  const [filtroCliente, setFiltroCliente] = useState('');    // cliente_id ('' = todos)

  const load = useCallback(async () => {
    if (esAgenteRestringido && !agenteScope) return;
    // Cargar cuotas y agrupar por póliza
    // filtroPago: 'pendiente' = solo con saldo, 'pagado' = solo al día (sin pendientes), '' = todas
    const estadosFiltro = filtroPago === 'pagado'
      ? ['pagado']
      : filtroPago === 'pendiente'
        ? ['pendiente', 'parcial']
        : ['pendiente', 'parcial', 'pagado'];

    // Traer TODOS los pagos relevantes PAGINANDO. Supabase corta cada request en ~1000
    // filas; como se ordena por fecha ascendente, sin paginar las cuotas más NUEVAS
    // (renovaciones 2026+) se quedaban fuera del corte y no aparecían en Cobros.
    // Además se aplica el filtro por estado (antes se calculaba pero no se usaba).
    const pageSize = 1000;
    let data = [];
    for (let desde = 0; ; desde += pageSize) {
      let pq = supabase.from('pagos')
        .select('*, polizas(numero, prima, estado, tipo, aseguradora_id, cliente_id, aseguradoras(nombre), clientes(nombre))')
        .in('estado', estadosFiltro)
        .order('fecha_vencimiento', { ascending: true })
        .range(desde, desde + pageSize - 1);
      if (esAgenteRestringido) pq = pq.in('poliza_id', agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]);
      const { data: pagina, error } = await pq;
      if (error || !pagina?.length) break;
      data = data.concat(pagina);
      if (pagina.length < pageSize) break;
    }

    const byPoliza = {};
    for (const p of (data || [])) {
      const pid = p.poliza_id;
      const estadoPoliza = p.polizas?.estado;

      // Excluir pólizas canceladas de la vista de cobros
      // Excepción futura: endosos de cancelación con prima pendiente legítima
      if (estadoPoliza === 'cancelada') continue;

      if (!byPoliza[pid]) {
        byPoliza[pid] = {
          poliza_id:           pid,
          poliza_num:          p.polizas?.numero,
          cliente:             p.polizas?.clientes?.nombre,
          cliente_id:          p.polizas?.cliente_id,
          aseguradora_id:      p.polizas?.aseguradora_id,
          aseguradora_nombre:  p.polizas?.aseguradoras?.nombre,
          saldo_total:         0,
          saldo_a_fecha:       0,
          cuotas_vencidas:     0,
          total_pagado:        0,
          proxima_vencimiento: null,
          tiene_pendientes:    false,
          id: p.id,
        };
      }
      const faltante = Math.round(((parseFloat(p.monto)||0) - (parseFloat(p.monto_pagado)||0)) * 100) / 100;
      if (['pendiente','parcial'].includes(p.estado)) {
        byPoliza[pid].saldo_total = Math.round((byPoliza[pid].saldo_total + faltante) * 100) / 100;
        byPoliza[pid].tiene_pendientes = true;
        // Vencidas = estrictamente atrasadas (su fecha ya pasó).
        if (p.fecha_vencimiento && p.fecha_vencimiento < hoy()) {
          byPoliza[pid].cuotas_vencidas++;
        }
        // Saldo del mes = lo que el cliente debe pagar hasta el cierre de este mes
        // (incluye lo vencido + la cuota cuyo periodo arranca este mes).
        if (p.fecha_vencimiento && p.fecha_vencimiento <= finMes()) {
          byPoliza[pid].saldo_a_fecha = Math.round((byPoliza[pid].saldo_a_fecha + faltante) * 100) / 100;
        }
        if (!byPoliza[pid].proxima_vencimiento || p.fecha_vencimiento < byPoliza[pid].proxima_vencimiento) {
          byPoliza[pid].proxima_vencimiento = p.fecha_vencimiento;
        }
      } else if (p.estado === 'pagado') {
        byPoliza[pid].total_pagado = Math.round((byPoliza[pid].total_pagado + (parseFloat(p.monto)||0)) * 100) / 100;
      }
    }

    let arr = Object.values(byPoliza);
    // Aplicar filtro de vista
    if (filtroPago === 'pendiente') arr = arr.filter(r => r.tiene_pendientes);
    else if (filtroPago === 'pagado')  arr = arr.filter(r => !r.tiene_pendientes && r.total_pagado > 0);
    // El filtro de póliza es por selección (PolizaSelect), no por texto en vivo.
    if (filtroPoliza) arr = arr.filter(p => String(p.poliza_id) === String(filtroPoliza));
    if (filterAseg) arr = arr.filter(p => String(p.aseguradora_id) === String(filterAseg));
    if (filtroCliente) arr = arr.filter(p => String(p.cliente_id) === String(filtroCliente));
    // Primero morosas, luego por próximo vencimiento
    arr.sort((a, b) => {
      if (b.cuotas_vencidas !== a.cuotas_vencidas) return b.cuotas_vencidas - a.cuotas_vencidas;
      return (a.proxima_vencimiento||'').localeCompare(b.proxima_vencimiento||'');
    });
    setRows(arr);
  }, [filtroPoliza, filterAseg, filtroCliente, filtroPago, esAgenteRestringido, agenteScope]);

  // ── Ordenamiento por columnas (clic en encabezado) ──
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const sortVal = (r, col) => {
    switch (col) {
      case 'poliza':      return (r.poliza_num || '').toLowerCase();
      case 'cliente':     return (r.cliente || '').toLowerCase();
      case 'aseguradora': return (r.aseguradora_nombre || '').toLowerCase();
      case 'saldo_total': return r.tiene_pendientes ? (r.saldo_total || 0) : (r.total_pagado || 0);
      case 'saldo_mes':   return r.saldo_a_fecha || 0;
      case 'vencidas':    return r.cuotas_vencidas || 0;
      case 'proxima':     return r.proxima_vencimiento || '';
      default:            return '';
    }
  };
  const sortedRows = (() => {
    if (!sortCol) return rows; // orden por defecto (morosas primero) que arma load()
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = sortVal(a, sortCol), vb = sortVal(b, sortCol);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  useEffect(() => {
    load();
    // Cargar catálogo de aseguradoras una sola vez
    supabase.from('aseguradoras').select('id, nombre').eq('activa', true).order('nombre')
      .then(({ data }) => setAseguradoras(data || []));
  }, [load]);

  const openCreate = async () => {
    const { data } = await supabase.from('polizas').select('id, numero, clientes(nombre)').in('estado',['activa','en_renovacion']).order('numero');
    setPolizas((data||[]).map(p=>({id:p.id, numero:p.numero, cliente:p.clientes?.nombre})));
    setForm(EMPTY_PAGO_FORM);
    setModal('create');
  };

  const handleSaveNuevo = async () => {
    if (!form.poliza_id || !form.monto) return;
    try {
      await insertRow('pagos', {
        poliza_id: parseInt(form.poliza_id), monto: parseFloat(form.monto),
        estado: form.estado, fecha_vencimiento: form.fecha_vencimiento || null,
        metodo: form.metodo || null, notas: form.notas || null,
      });
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message||'')); }
  };

  // Abre la ventana compartida de detalle de cobros para esta póliza.
  const openDetalle = (r) => setDetalleRef({ poliza_id: r.poliza_id, poliza_num: r.poliza_num, cliente: r.cliente });

  const morosos = rows.filter(r => r.cuotas_vencidas > 0).length;
  // Búsqueda primero: la tabla no lista pólizas hasta elegir cliente, póliza o
  // aseguradora (el selector de saldo es un modo de vista, no una búsqueda).
  const hayFiltro = !!(filtroCliente || filtroPoliza || filterAseg);

  return (
    <div>
      <div className="toolbar">
        <PolizaSelect value={filtroPoliza} onChange={setFiltroPoliza} width={230} />
        <ClienteSelect value={filtroCliente} onChange={setFiltroCliente} placeholder="Filtrar por cliente..." width={230} />
        <select className="form-select" style={{ width:180 }} value={filtroPago} onChange={e=>setFiltroPago(e.target.value)}>
          <option value="pendiente">Con saldo pendiente</option>
          <option value="pagado">Al día (sin pendientes)</option>
          <option value="">Todas las pólizas</option>
        </select>
        <select className="form-select" style={{ width:190 }} value={filterAseg} onChange={e=>setFilterAseg(e.target.value)}>
          <option value="">Todas las aseguradoras</option>
          {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
      </div>
      {morosos > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(239, 68, 68, 0.1)', borderRadius:8, marginBottom:16, border:'1px solid rgba(239, 68, 68, 0.25)', fontSize:13, color:'var(--text-danger)' }}>
          <AlertTriangle size={14}/> {morosos} póliza(s) con cuotas vencidas sin pagar
        </div>
      )}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {[['poliza','Póliza'],['cliente','Cliente'],['aseguradora','Aseguradora'],
                ['saldo_total','Saldo Total'],['saldo_mes','Saldo del mes'],
                ['vencidas','Vencidas'],['proxima','Próx. Vencimiento']].map(([col,label]) => (
                <th key={col} onClick={()=>handleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} title="Ordenar">
                  {label}{sortIcon(col)}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!hayFiltro
              ? <tr><td colSpan={8}><div className="empty-state"><div className="empty-state-icon"><Search size={40}/></div><div className="empty-state-title">Busca para ver cobros</div><div>Elige un cliente, una póliza o una aseguradora. La lista no muestra todas las pólizas de una vez.</div></div></td></tr>
              : sortedRows.length === 0
              ? <tr><td colSpan={8}><div className="empty-state"><div className="empty-state-icon"><CreditCard size={40}/></div><div className="empty-state-title">Sin resultados</div></div></td></tr>
              : sortedRows.map(r => {
                const moroso = r.cuotas_vencidas > 0;
                const tieneSaldoMes = r.saldo_a_fecha > 0;
                return (
                  <tr key={r.poliza_id} style={moroso ? {background:'rgba(239, 68, 68, 0.04)'} : {}}>
                    <td>
                      <button onClick={() => verPolizaCompleta(r.poliza_id)} style={{ background:'none', border:'none', cursor:'pointer', fontWeight:700, color:'var(--sky)', padding:0, fontFamily:'inherit', fontSize:'inherit' }}>
                        {r.poliza_num || '—'}
                      </button>
                    </td>
                    <td>{r.cliente}</td>
                    <td style={{ fontSize:12, color:'var(--silver)' }}>{r.aseguradora_nombre || '—'}</td>
                    <td style={{ fontWeight:700, color: r.tiene_pendientes ? '#f59e0b' : '#22c55e' }}>
                      {r.tiene_pendientes ? fmt$(r.saldo_total) : 'Al día'}
                    </td>
                    <td style={{ fontWeight:700, color: moroso ? '#ef4444' : (tieneSaldoMes ? '#f59e0b' : 'var(--silver)') }}>
                      {tieneSaldoMes ? fmt$(r.saldo_a_fecha) : '—'}
                    </td>
                    <td style={{ color: moroso ? '#ef4444' : 'var(--silver)', fontSize:12 }}>
                      {moroso ? <span style={{display:'flex',alignItems:'center',gap:4}}><AlertTriangle size={12}/>{r.cuotas_vencidas}</span> : '—'}
                    </td>
                    <td style={{ color: moroso ? '#ef4444' : 'inherit', fontSize:13 }}>
                      {fmtFecha(r.proxima_vencimiento) || '—'}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => openDetalle(r)} title="Ver detalle de cuotas">
                        <Eye size={12}/> Ver
                      </button>
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>

      {/* Modal: Nuevo Cobro Manual */}
      {modal==='create' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Nuevo Cobro Manual</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Póliza *</label>
                  <select className="form-select" value={form.poliza_id} onChange={e=>setForm(p=>({...p,poliza_id:e.target.value}))}>
                    <option value="">Seleccionar...</option>
                    {polizas.map(p=><option key={p.id} value={p.id}>{p.numero} — {p.cliente}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Monto *</label>
                  <input className="form-input" type="number" step="0.01" value={form.monto} onChange={e=>setForm(p=>({...p,monto:e.target.value}))} placeholder="0.00"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Vencimiento</label>
                  <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e=>setForm(p=>({...p,fecha_vencimiento:e.target.value}))}/>
                </div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea" value={form.notas} onChange={e=>setForm(p=>({...p,notas:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSaveNuevo}>Guardar</button>
            </div>
          </div>
        </div>
      )}
      {/* Ventana de detalle de cobros (compartida con Morosidad) */}
      {detalleRef && (
        <DetalleCobrosModal polizaRef={detalleRef} onClose={() => setDetalleRef(null)} onChanged={load} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// TAB: ESTADO DE CUENTA
// ──────────────────────────────────────────────────────────────
function TabEstadoCuenta() {
  const { agenteScope, esAgenteRestringido } = useApp();
  const [clientes, setClientes] = useState([]);
  const [ramos, setRamos]       = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [polizasCliente, setPolizasCliente] = useState([]); // pólizas del cliente (para el selector)
  const [polizaSel, setPolizaSel] = useState('');           // '' = todas, o id de una póliza específica
  const [filtroRamo, setFiltroRamo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [desde, setDesde]       = useState('');
  const [hasta, setHasta]       = useState('');
  const [polizasData, setPolizasData] = useState([]);
  const [pagosData, setPagosData] = useState([]);
  const [cobrosData, setCobrosData] = useState([]);
  const [endososData, setEndososData] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    let cq = supabase.from('clientes').select('id, nombre').order('nombre');
    if (esAgenteRestringido) cq = cq.in('id', agenteScope?.clienteIds?.length ? agenteScope.clienteIds : [-1]);
    Promise.all([
      cq,
      supabase.from('ramos').select('id, nombre').eq('activo', true).order('nombre'),
    ]).then(([c, r]) => { setClientes(c.data||[]); setRamos(r.data||[]); });
  }, [esAgenteRestringido, agenteScope]);

  // Al cambiar de cliente, cargar sus pólizas para el selector "una póliza específica"
  useEffect(() => {
    setPolizaSel(''); setPolizasCliente([]);
    if (!clienteId) return;
    supabase.from('polizas').select('id, numero, ramos(nombre)').eq('cliente_id', clienteId).order('numero', { ascending:true })
      .then(({ data }) => setPolizasCliente(data || []));
  }, [clienteId]);

  const buscar = async () => {
    if (!clienteId) return;
    setCargando(true);
    let q = supabase.from('polizas')
      .select('*, ramos(nombre), aseguradoras(nombre)')
      .eq('cliente_id', clienteId);
    if (polizaSel)    q = q.eq('id', polizaSel);
    if (filtroRamo)   q = q.eq('ramo_id', filtroRamo);
    if (filtroEstado) q = q.eq('estado', filtroEstado);
    if (desde) q = q.gte('fecha_inicio', desde);
    if (hasta) q = q.lte('fecha_vencimiento', hasta);
    if (esAgenteRestringido) q = q.in('id', agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]);
    const { data: pols } = await q.order('numero', { ascending:true });   // ordenadas por póliza
    setPolizasData(pols || []);

    if (pols && pols.length > 0) {
      const ids = pols.map(p => p.id);
      const [{ data: pagos }, { data: cobros }, { data: endosos }] = await Promise.all([
        supabase.from('pagos').select('*').in('poliza_id', ids),
        supabase.from('cobros_recibidos').select('*').in('poliza_id', ids).order('fecha', { ascending:true }),
        supabase.from('poliza_endosos').select('*').in('poliza_id', ids).order('fecha', { ascending:true }),
      ]);
      setPagosData(pagos || []); setCobrosData(cobros || []); setEndososData(endosos || []);
    } else { setPagosData([]); setCobrosData([]); setEndososData([]); }
    setCargando(false);
  };

  const getPagos  = (id) => pagosData.filter(p => p.poliza_id === id);
  const getCobros = (id) => cobrosData.filter(c => c.poliza_id === id && !c.anulado)
    .sort((a,b)=>String(a.fecha||'').localeCompare(String(b.fecha||'')));
  // Endosos que MOVIERON prima (delta_prima != 0): afectan el saldo, por eso se muestran
  const getEndososPrima = (id) => endososData.filter(e =>
    e.poliza_id === id && e.estado !== 'anulado' && Math.abs(parseFloat(e.delta_prima)||0) > 0.001);

  // Cálculo por póliza basado en el plan de cuotas (refleja los endosos que diluyeron prima)
  const calc = (pol) => {
    const ps = getPagos(pol.id);
    const prima = parseFloat(pol.prima)||0;
    const imp = prima * (parseFloat(pol.impuesto)||0)/100;
    const facturadoCuotas = ps.reduce((s,p)=>s+(parseFloat(p.monto)||0),0);
    const facturado = facturadoCuotas > 0.001 ? facturadoCuotas : (prima + imp);
    const pagado = ps.reduce((s,p)=>s + (p.estado==='pagado' ? (parseFloat(p.monto)||0) : (parseFloat(p.monto_pagado)||0)), 0);
    const saldo = facturado - pagado;
    return { prima, imp, facturado, pagado, saldo };
  };

  const tot = polizasData.reduce((a,pol)=>{ const c=calc(pol); a.facturado+=c.facturado; a.pagado+=c.pagado; a.saldo+=c.saldo; return a; }, { facturado:0, pagado:0, saldo:0 });
  const cliente = clientes.find(c=>String(c.id)===String(clienteId));

  // ── Estado de cuenta como LIBRO DE MOVIMIENTOS con saldo corrido ──
  // Todo lo que mueve el saldo es una línea: emisión de la póliza (facturación),
  // endosos que movieron prima, letras de facturación periódica / declaraciones /
  // regularizaciones, y los pagos del cliente. Las letras con origen 'endoso' NO
  // se listan aparte (el movimiento del endoso ya las representa) y la emisión se
  // calcula restando endosos y letras especiales del total facturado, porque los
  // endosos diluidos ya viven dentro de las cuotas (evita duplicar).
  const ORIGEN_LABEL = { periodo:'Factura del período', ajuste:'Regularización', declaracion:'Declaración', deposito:'Depósito de prima' };
  const movsPoliza = (pol) => {
    const r2 = n => Math.round((Number(n)||0)*100)/100;
    // Cada línea del libro mueve el saldo con su monto CON IMPUESTO, de modo que la suma
    // de los cargos cuadre EXACTO con el total facturado. Las letras (filas de `pagos`) ya
    // guardan su monto con impuesto; la prima sin imp. de un endoso se lleva a con imp.
    const impPct = (parseFloat(pol.impuesto)||0)/100;
    const conImp = (sinImp) => r2((parseFloat(sinImp)||0) * (1 + impPct));
    const cobs   = getCobros(pol.id);
    const pagos  = getPagos(pol.id);
    const { facturado } = calc(pol);

    // Letras con fila propia en `pagos`: declaraciones/endosos (origen 'endoso') y
    // facturas de período / regularizaciones / depósitos. Se muestran con su monto (con imp).
    const letrasEndoso = pagos.filter(p => p.origen === 'endoso');
    const letrasOtras  = pagos.filter(p => ORIGEN_LABEL[p.origen]);
    // Endosos que DILUYERON prima dentro de las cuotas (modelo viejo): no tienen letra
    // propia. Se muestran aparte CON IMPUESTO; su dilución ya vive en las cuotas.
    const endosoConLetra = new Set(letrasEndoso.map(p => p.endoso_id).filter(Boolean));
    const endosDiluidos = getEndososPrima(pol.id).filter(e => !endosoConLetra.has(e.id));
    const sumEndosDil  = endosDiluidos.reduce((s,e)=>s+conImp(e.delta_prima),0);
    const sumLetrasEnd = letrasEndoso.reduce((s,p)=>s+(parseFloat(p.monto)||0),0);
    const sumLetrasOtr = letrasOtras.reduce((s,p)=>s+(parseFloat(p.monto)||0),0);
    // Emisión = lo facturado que no viene de letras sueltas ni de endosos diluidos.
    const emision = r2(facturado - sumEndosDil - sumLetrasEnd - sumLetrasOtr);
    // Lookup del endoso ligado a cada letra (para etiquetarla).
    const endMap = {}; endososData.filter(e=>e.poliza_id===pol.id).forEach(e=>{ endMap[e.id]=e; });

    const movs = [];
    if (Math.abs(emision) > 0.005) {
      movs.push({ fecha: pol.fecha_emision || pol.fecha_inicio || '', mov:'Emisión de póliza', det:'Prima + impuesto', monto: emision });
    }
    for (const e of endosDiluidos) {
      movs.push({ fecha: e.fecha || '', mov:`Endoso${e.numero_endoso ? ' N° '+e.numero_endoso : ''}`,
        det: e.descripcion || (e.tipo==='cancelacion'?'Cancelación':e.tipo==='prorroga'?'Prórroga':''), monto: conImp(e.delta_prima) });
    }
    for (const p of letrasEndoso) {
      const e = endMap[p.endoso_id];
      const etq = pol.es_declarativa ? `Declaración${e?.numero_endoso ? ' N° '+e.numero_endoso : ''}` : `Endoso${e?.numero_endoso ? ' N° '+e.numero_endoso : ''}`;
      movs.push({ fecha: p.fecha_vencimiento || '', mov: etq, det: (e?.descripcion || p.notas || ''), monto: parseFloat(p.monto)||0 });
    }
    for (const p of letrasOtras) {
      movs.push({ fecha: p.fecha_vencimiento || '', mov: ORIGEN_LABEL[p.origen], det: p.notas || '', monto: parseFloat(p.monto)||0 });
    }
    for (const c of cobs) {
      const rec = c.num_recibo_aseg || c.num_recibo_ru;
      movs.push({ fecha: c.fecha || '', mov:'Pago del cliente',
        det: [c.forma_pago, etiquetaDondePago(c.donde_pago), rec ? 'Rec. '+rec : null].filter(Boolean).join(' · '),
        monto: -(parseFloat(c.monto)||0), esPago:true });
    }
    // Orden cronológico; en el mismo día primero los cargos, luego los pagos
    movs.sort((a,b)=>(a.fecha||'').localeCompare(b.fecha||'') || (a.esPago?1:0)-(b.esPago?1:0));
    let saldo = 0;
    for (const m of movs) { saldo = r2(saldo + m.monto); m.saldo = saldo; }
    return movs;
  };

  // ── Impresión: incluye pagos del cliente y endosos que movieron prima, por póliza ──
  const imprimir = () => {
    const M = marcaImpresion();
    if (!polizasData.length) return;
    const bloques = polizasData.map(pol => {
      const c = calc(pol);
      const movs = movsPoliza(pol);
      const movsHtml = movs.length ? `
        <div class="sub">Movimientos de la cuenta</div>
        <table><thead><tr><th>Fecha</th><th>Movimiento</th><th>Detalle</th><th class="r">Facturado</th><th class="r">Crédito</th><th class="r">Saldo</th></tr></thead><tbody>
        ${movs.map(m=>`<tr>
          <td>${fmtFecha(m.fecha)||'—'}</td>
          <td>${m.mov}</td>
          <td>${m.det||''}</td>
          <td class="r">${m.monto>0?fmt$(m.monto):''}</td>
          <td class="r" style="color:#16a34a">${m.monto<0?fmt$(-m.monto):''}</td>
          <td class="r" style="font-weight:700;color:${m.saldo>0.001?'#111':'#16a34a'}">${fmt$(m.saldo)}</td>
        </tr>`).join('')}
        </tbody></table>` : '<div class="muted">Sin movimientos registrados.</div>';
      return `
        <div class="pol">
          <div class="polhead">
            <div><span class="polnum">Póliza ${pol.numero||'—'}</span> · ${pol.ramos?.nombre||''} · ${pol.aseguradoras?.nombre||''}</div>
            <div class="muted">Vigencia: ${fmtFecha(pol.fecha_inicio)||'—'} a ${fmtFecha(pol.fecha_vencimiento)||'—'} · Estado: ${pol.estado||'—'}</div>
          </div>
          <div class="cards">
            <div class="card"><div class="cl">Total a pagar</div><div class="cv">${fmt$(c.facturado)}</div></div>
            <div class="card"><div class="cl">Pagado</div><div class="cv" style="color:#16a34a">${fmt$(c.pagado)}</div></div>
            <div class="card"><div class="cl">Saldo</div><div class="cv" style="color:${c.saldo>0.001?'#dc2626':'#16a34a'}">${fmt$(c.saldo)}</div></div>
          </div>
          ${movsHtml}
        </div>`;
    }).join('');
    const scope = polizaSel ? `Póliza ${polizasData[0]?.numero||''}` : `Todas las pólizas (${polizasData.length})`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Estado de Cuenta — ${cliente?.nombre||''}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:22px}
        .brand{display:flex;align-items:center;gap:10px;margin-bottom:12px;border-bottom:2px solid ${M.colores.steel};padding-bottom:10px}
        .brand-name{font-size:16px;font-weight:800;color:${M.colores.steel};letter-spacing:1px}
        .brand-tag{font-size:10px;color:${M.colores.sky}}
        h1{font-size:16px;margin:0 0 2px}
        .meta{font-size:11px;color:#555;margin-bottom:14px}
        .topcards{display:flex;gap:12px;margin-bottom:18px}
        .topcard{border:1px solid #e0e0e0;border-radius:8px;padding:10px 16px;min-width:120px}
        .topcard .cl{font-size:10px;color:#666}.topcard .cv{font-size:16px;font-weight:700}
        .pol{border:1px solid #d8dee6;border-radius:8px;padding:12px 14px;margin-bottom:14px;page-break-inside:avoid}
        .polhead{margin-bottom:8px}
        .polnum{font-weight:800;color:${M.colores.steel};font-size:13px}
        .muted{font-size:10px;color:#777}
        .cards{display:flex;gap:10px;margin:8px 0}
        .card{border:1px solid #e6e9ef;border-radius:6px;padding:6px 12px}
        .card .cl{font-size:9px;color:#666}.card .cv{font-size:13px;font-weight:700}
        .sub{font-size:11px;font-weight:700;color:${M.colores.steel};margin:10px 0 4px}
        table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:4px}
        th{background:${M.colores.steel};color:#fff;padding:5px 6px;text-align:left}
        td{padding:4px 6px;border-bottom:1px solid #e6e6e6}
        .r{text-align:right}
        tr:nth-child(even) td{background:#f7f9fb}
        @media print{body{margin:12px}}
      </style></head><body>
      <div class="brand">${M.logo}<div><div class="brand-name" style="text-transform:uppercase">${M.nombre}</div><div class="brand-tag">${M.tagline}</div></div></div>
      <h1>Estado de Cuenta</h1>
      <div class="meta">${cliente?.nombre||''} · ${scope} · Generado: ${fmtFecha(new Date())}</div>
      <div class="topcards">
        <div class="topcard"><div class="cl">Total facturado</div><div class="cv" style="color:${M.colores.sky}">${fmt$(tot.facturado)}</div></div>
        <div class="topcard"><div class="cl">Total pagado</div><div class="cv" style="color:#16a34a">${fmt$(tot.pagado)}</div></div>
        <div class="topcard"><div class="cl">Saldo pendiente</div><div class="cv" style="color:${tot.saldo>0.001?'#dc2626':'#16a34a'}">${fmt$(tot.saldo)}</div></div>
      </div>
      ${bloques}
      </body></html>`;
    imprimirHTML(html);
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{ background:'rgba(var(--navy-rgb),0.4)', borderRadius:10, padding:'16px 20px', marginBottom:20 }}>
        <div className="form-grid form-grid-3" style={{ marginBottom:12 }}>
          <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
            <label className="form-label">Cliente *</label>
            <ClienteSelect options={clientes} value={clienteId} onChange={id=>setClienteId(id)}
              placeholder="Escribe el nombre del cliente..." />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Póliza</label>
            <select className="form-select" value={polizaSel} onChange={e=>setPolizaSel(e.target.value)} disabled={!clienteId}>
              <option value="">Todas las pólizas</option>
              {polizasCliente.map(p=><option key={p.id} value={p.id}>{p.numero}{p.ramos?.nombre?` — ${p.ramos.nombre}`:''}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Ramo</label>
            <select className="form-select" value={filtroRamo} onChange={e=>setFiltroRamo(e.target.value)}>
              <option value="">Todos</option>
              {ramos.map(r=><option key={r.id} value={r.id}>{r.nombre}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Estado de Póliza</label>
            <select className="form-select" value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}>
              <option value="">Todos</option>
              <option value="activa">Activa</option>
              <option value="vencida">Vencida</option>
              <option value="no_renovada">No renovada</option>
              <option value="en_renovacion">En renovación</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Vigencia desde</label>
            <input className="form-input" type="date" value={desde} onChange={e=>setDesde(e.target.value)}/>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Vigencia hasta</label>
            <input className="form-input" type="date" value={hasta} onChange={e=>setHasta(e.target.value)}/>
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-primary btn-sm" onClick={buscar} disabled={!clienteId||cargando}>
            {cargando ? 'Buscando...' : 'Generar Estado de Cuenta'}
          </button>
          {polizasData.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={imprimir}>
              <Printer size={13}/> Imprimir / PDF
            </button>
          )}
        </div>
      </div>

      {/* Resultado */}
      {polizasData.length > 0 && (
        <div>
          {/* Encabezado + resumen */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:16 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>Estado de Cuenta</div>
              <div style={{ fontSize:14, color:'var(--silver)', marginTop:4 }}>{cliente?.nombre} · {polizaSel ? `Póliza ${polizasData[0]?.numero||''}` : `${polizasData.length} póliza(s)`}</div>
              <div style={{ fontSize:12, color:'var(--silver)' }}>Generado: {fmtFecha(new Date())}</div>
            </div>
            <div style={{ display:'flex', gap:16 }}>
              {[
                ['Total Facturado', fmt$(tot.facturado), 'var(--sky-light)'],
                ['Total Pagado', fmt$(tot.pagado), '#22c55e'],
                ['Saldo Pendiente', fmt$(tot.saldo), tot.saldo>0.001?'#ef4444':'#22c55e'],
              ].map(([l,v,col]) => (
                <div key={l} style={{ background:'rgba(var(--navy-rgb),0.6)', borderRadius:10, padding:'12px 18px', textAlign:'center', minWidth:130 }}>
                  <div style={{ fontSize:11, color:'var(--silver)', marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:18, fontWeight:700, color:col }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Detalle por póliza (ordenado por número) */}
          {polizasData.map(pol => {
            const c = calc(pol);
            const movs = movsPoliza(pol);
            return (
              <div key={pol.id} style={{ background:'rgba(var(--navy-rgb),0.4)', borderRadius:10, padding:'14px 18px', marginBottom:16, border:'1px solid rgba(var(--silver-rgb),0.12)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:10 }}>
                  <div>
                    <span style={{ fontWeight:800, color:'var(--sky)', fontSize:15 }}>Póliza {pol.numero||'—'}</span>
                    <span style={{ color:'var(--silver)', marginLeft:8 }}>{pol.ramos?.nombre} · {pol.aseguradoras?.nombre}</span>
                    <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                      Vigencia {fmtFecha(pol.fecha_inicio)||'—'} a {fmtFecha(pol.fecha_vencimiento)||'—'} · <span className={`badge ${pol.estado==='activa'?'badge-success':pol.estado==='vencida'?'badge-danger':'badge-neutral'}`}>{etiquetaEstadoPoliza(pol.estado)}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:14, fontSize:13, flexWrap:'wrap' }}>
                    <span style={{ color:'var(--silver)' }}>Total: <strong style={{color:'var(--text)'}}>{fmt$(c.facturado)}</strong></span>
                    <span style={{ color:'var(--silver)' }}>Pagado: <strong style={{color:'var(--text-success)'}}>{fmt$(c.pagado)}</strong></span>
                    <span style={{ color:'var(--silver)' }}>Saldo: <strong style={{color:c.saldo>0.001?'#ef4444':'#22c55e'}}>{fmt$(c.saldo)}</strong></span>
                  </div>
                </div>

                {/* Movimientos de la cuenta: emisión, endosos, letras y pagos con saldo corrido */}
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:6 }}>MOVIMIENTOS DE LA CUENTA</div>
                  {movs.length === 0 ? (
                    <div style={{ fontSize:12, color:'var(--silver)' }}>Sin movimientos registrados.</div>
                  ) : (
                    <div className="table-wrapper">
                      <table>
                        <thead><tr>
                          <th>Fecha</th><th>Movimiento</th><th>Detalle</th>
                          <th style={{textAlign:'right'}}>Facturado</th>
                          <th style={{textAlign:'right'}}>Crédito</th>
                          <th style={{textAlign:'right'}}>Saldo</th>
                        </tr></thead>
                        <tbody>
                          {movs.map((m,i)=>(
                            <tr key={i}>
                              <td style={{fontSize:12}}>{fmtFecha(m.fecha)||'—'}</td>
                              <td style={{fontSize:12, fontWeight:600, color: m.esPago?'#22c55e':'inherit'}}>{m.mov}</td>
                              <td style={{fontSize:12, color:'var(--silver)'}}>{m.det||'—'}</td>
                              <td style={{textAlign:'right', fontWeight:600, color:'var(--text-warning)'}}>{m.monto>0?fmt$(m.monto):''}</td>
                              <td style={{textAlign:'right', fontWeight:600, color:'var(--text-success)'}}>{m.monto<0?fmt$(-m.monto):''}</td>
                              <td style={{textAlign:'right', fontWeight:700, color: m.saldo>0.001?'#f59e0b':'#22c55e'}}>{fmt$(m.saldo)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {polizasData.length === 0 && clienteId && !cargando && (
        <div className="empty-state"><div className="empty-state-icon"><FileText size={40}/></div><div className="empty-state-title">Sin pólizas para los filtros seleccionados</div></div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// TAB: MOROSIDAD
// ──────────────────────────────────────────────────────────────
function TabMorosidad() {
  const { agenteScope, esAgenteRestringido } = useApp();
  const [rows, setRows]         = useState([]);
  const [diasFiltro, setDiasFiltro] = useState('30');
  const [cargando, setCargando] = useState(false);
  const [agentes, setAgentes]   = useState([]);
  const [filterAgente, setFilterAgente] = useState('');
  const [aseguradoras, setAseguradoras] = useState([]);
  const [filterAseg, setFilterAseg] = useState('');
  const [sortCol, setSortCol] = useState('');   // columna de orden (clic en encabezado)
  const [sortDir, setSortDir] = useState('asc');
  const [detalleRef, setDetalleRef] = useState(null); // póliza abierta en el detalle de cobros
  const [filtroCliente, setFiltroCliente] = useState('');  // cliente_id ('' = todos)

  useEffect(() => {
    supabase.from('agentes').select('id, nombre').eq('activo', true).order('nombre')
      .then(({ data }) => setAgentes(data || []));
    supabase.from('aseguradoras').select('id, nombre').eq('activa', true).order('nombre')
      .then(({ data }) => setAseguradoras(data || []));
  }, []);

  const load = async () => {
    if (esAgenteRestringido && !agenteScope) { setRows([]); return; }
    setCargando(true);
    const corte = new Date(); corte.setDate(corte.getDate() - parseInt(diasFiltro));
    const corteStr = corte.toISOString().slice(0,10);
    let mq = supabase.from('pagos')
      .select('*, polizas(numero, estado, cliente_id, aseguradora_id, fecha_inicio, fecha_vencimiento, clientes(nombre), ramos(nombre), aseguradoras(nombre), poliza_agentes(agente_id, agentes(nombre)))')
      .eq('estado','pendiente')
      .lte('fecha_vencimiento', hoy())
      .gte('fecha_vencimiento', corteStr)
      .order('fecha_vencimiento', { ascending:true });
    if (esAgenteRestringido) mq = mq.in('poliza_id', agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]);
    const { data } = await mq;
    // Excluir cuotas de pólizas canceladas
    setRows((data||[])
      .filter(p => p.polizas?.estado !== 'cancelada')
      .map(p=>{
        const pa = p.polizas?.poliza_agentes || [];
        return {
          ...p, poliza_num:p.polizas?.numero, cliente:p.polizas?.clientes?.nombre, ramo:p.polizas?.ramos?.nombre,
          cliente_id: p.polizas?.cliente_id,
          aseguradora_id: p.polizas?.aseguradora_id,
          aseguradora: p.polizas?.aseguradoras?.nombre,
          vig_inicio: p.polizas?.fecha_inicio,
          vig_fin: p.polizas?.fecha_vencimiento,
          agente_ids: pa.map(a => a.agente_id),
          agentes_nombres: pa.map(a => a.agentes?.nombre).filter(Boolean),
        };
      }));
    setCargando(false);
  };

  useEffect(() => { load(); }, [diasFiltro, esAgenteRestringido, agenteScope]); // eslint-disable-line

  const diasVencido = (fecha) => Math.floor((new Date() - new Date(fecha)) / 86400000);
  // Filtro por agente (una póliza puede tener varios agentes), aseguradora y cliente
  let rowsFiltradas = filterAgente
    ? rows.filter(r => (r.agente_ids || []).some(id => String(id) === String(filterAgente)))
    : rows;
  if (filterAseg) rowsFiltradas = rowsFiltradas.filter(r => String(r.aseguradora_id) === String(filterAseg));
  if (filtroCliente) rowsFiltradas = rowsFiltradas.filter(r => String(r.cliente_id) === String(filtroCliente));

  // ── Ordenamiento por columnas (clic en encabezado) ──
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const sortVal = (r, col) => {
    switch (col) {
      case 'cliente':     return (r.cliente || '').toLowerCase();
      case 'poliza':      return (r.poliza_num || '').toLowerCase();
      case 'ramo':        return (r.ramo || '').toLowerCase();
      case 'aseguradora': return (r.aseguradora || '').toLowerCase();
      case 'agentes':     return (r.agentes_nombres || []).join(', ').toLowerCase();
      case 'monto':       return parseFloat(r.monto) || 0;
      case 'vencido':     return r.fecha_vencimiento || '';
      case 'dias':        return diasVencido(r.fecha_vencimiento);
      case 'vig_inicio':  return r.vig_inicio || '';
      case 'vig_fin':     return r.vig_fin || '';
      default:            return '';
    }
  };
  if (sortCol) {
    rowsFiltradas = [...rowsFiltradas].sort((a, b) => {
      const va = sortVal(a, sortCol), vb = sortVal(b, sortCol);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const totalMoroso = rowsFiltradas.reduce((s,r)=>s+(parseFloat(r.monto)||0),0);
  const agenteSelNombre = agentes.find(a => String(a.id) === String(filterAgente))?.nombre || '';
  const asegSelNombre = aseguradoras.find(a => String(a.id) === String(filterAseg))?.nombre || '';

  const exportarExcel = async () => {
    const M = marcaImpresion();
    const XLSX = await loadXLSX();
    const clienteSelNombre = filtroCliente ? (rowsFiltradas[0]?.cliente || '') : '';
    const aoa = [
      [`${M.nombre} — Morosidad`],
      [filterAgente ? `Agente: ${agenteSelNombre}` : 'Todos los agentes'],
      [filterAseg ? `Aseguradora: ${asegSelNombre}` : 'Todas las aseguradoras'],
      [filtroCliente ? `Cliente: ${clienteSelNombre}` : 'Todos los clientes'],
      [`Ventana: ${diasFiltro === '365' ? 'Todo' : diasFiltro + ' días'} · Generado: ${fmtFecha(new Date())}`],
      [],
      ['Cliente','Póliza','Ramo','Aseguradora','Agente(s)','Vig. inicial','Vig. final','Monto vencido','Vencimiento','Días vencido'],
    ];
    let total = 0;
    for (const r of rowsFiltradas) {
      total += parseFloat(r.monto) || 0;
      aoa.push([
        r.cliente || '', r.poliza_num || '', r.ramo || '', r.aseguradora || '',
        (r.agentes_nombres || []).join(', ') || 'Sin agente',
        r.vig_inicio ? String(r.vig_inicio).slice(0,10) : '',
        r.vig_fin ? String(r.vig_fin).slice(0,10) : '',
        parseFloat(r.monto) || 0,
        r.fecha_vencimiento ? String(r.fecha_vencimiento).slice(0,10) : '',
        diasVencido(r.fecha_vencimiento),
      ]);
    }
    aoa.push([]);
    aoa.push(['', '', '', '', '', '', 'TOTAL', total, '', '']);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{wch:26},{wch:18},{wch:16},{wch:20},{wch:24},{wch:12},{wch:12},{wch:14},{wch:13},{wch:12}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Morosidad');
    const safe = ((asegSelNombre ? asegSelNombre + '_' : '') + (agenteSelNombre || 'todos')).replace(/[^a-zA-Z0-9_]/g, '_');
    XLSX.writeFile(wb, `Morosidad_${safe}_${hoy()}.xlsx`);
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20, flexWrap:'wrap' }}>
        <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:'1px solid rgba(var(--silver-rgb),0.2)' }}>
          {[['30','30 días'],['60','60 días'],['90','90 días'],['365','Todo']].map(([v,l])=>(
            <button key={v} onClick={()=>setDiasFiltro(v)} style={{
              padding:'7px 14px', fontSize:12, border:'none', cursor:'pointer', fontFamily:'var(--font)',
              background:diasFiltro===v?'var(--sky)':'transparent', color:diasFiltro===v?'#fff':'var(--silver)',
            }}>{l}</button>
          ))}
        </div>
        <select className="form-select" style={{ width:220 }} value={filterAgente} onChange={e=>setFilterAgente(e.target.value)}>
          <option value="">Todos los agentes</option>
          {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <select className="form-select" style={{ width:220 }} value={filterAseg} onChange={e=>setFilterAseg(e.target.value)}>
          <option value="">Todas las aseguradoras</option>
          {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <ClienteSelect value={filtroCliente} onChange={setFiltroCliente} placeholder="Filtrar por cliente..." width={230} />
        <button className="btn btn-secondary btn-sm" onClick={exportarExcel} disabled={rowsFiltradas.length===0}
          title="Exportar la morosidad filtrada a Excel">
          <FileText size={13}/> Exportar Excel
        </button>
        {rowsFiltradas.length > 0 && (
          <div style={{ fontSize:13, color:'var(--text-danger)', fontWeight:600 }}>
            {rowsFiltradas.length} cobro(s) vencido(s) · {fmt$(totalMoroso)} pendiente
          </div>
        )}
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr>
            {[['cliente','Cliente'],['poliza','Póliza'],['ramo','Ramo'],['aseguradora','Aseguradora'],
              ['agentes','Agente(s)'],['vig_inicio','Vig. inicial'],['vig_fin','Vig. final'],
              ['monto','Monto'],['vencido','Vencido'],['dias','Días Vencido']].map(([col,label]) => (
              <th key={col} onClick={()=>handleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} title="Ordenar">
                {label}{sortIcon(col)}
              </th>
            ))}
            <th>Acciones</th>
          </tr></thead>
          <tbody>
            {cargando ? <tr><td colSpan={11}><div style={{ textAlign:'center', padding:24, color:'var(--silver)' }}>Cargando...</div></td></tr>
            : rowsFiltradas.length === 0 ? <tr><td colSpan={11}><div className="empty-state"><div className="empty-state-icon"><Check size={40} style={{color:'var(--text-success)'}}/></div><div className="empty-state-title">Sin morosidad en este período</div></div></td></tr>
            : rowsFiltradas.map(r => {
              const dias = diasVencido(r.fecha_vencimiento);
              const color = dias > 60 ? '#ef4444' : dias > 30 ? '#f59e0b' : 'var(--silver)';
              return (
                <tr key={r.id}>
                  <td style={{ fontWeight:600 }}>{r.cliente}</td>
                  <td style={{ color:'var(--sky)', fontWeight:700 }}>{r.poliza_num || '—'}</td>
                  <td className="text-silver">{r.ramo}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{r.aseguradora || '—'}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{(r.agentes_nombres||[]).join(', ') || '—'}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{fmtFecha(r.vig_inicio) || '—'}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{fmtFecha(r.vig_fin) || '—'}</td>
                  <td style={{ fontWeight:700, color:'var(--text-danger)' }}>{fmt$(r.monto)}</td>
                  <td>{fmtFecha(r.fecha_vencimiento)}</td>
                  <td><span style={{ fontWeight:700, color }}>{dias} días</span></td>
                  <td>
                    <button className="btn btn-sm btn-secondary" title="Ver detalle de cobros"
                      onClick={() => setDetalleRef({ poliza_id: r.poliza_id, poliza_num: r.poliza_num, cliente: r.cliente })}>
                      <Eye size={12}/> Ver
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Ventana de detalle de cobros (misma que en Cobros y Pagos); al cerrar
          se queda en Morosidad con sus filtros. onChanged refresca la lista. */}
      {detalleRef && (
        <DetalleCobrosModal polizaRef={detalleRef} onClose={() => setDetalleRef(null)} onChanged={load} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// MÓDULO PRINCIPAL: COBROS
// ──────────────────────────────────────────────────────────────
const TABS = [
  { key:'pagos',    label:'Cobros y Pagos',   icon:CreditCard },
  { key:'estado',   label:'Estado de Cuenta', icon:FileText },
  { key:'moroso',   label:'Morosidad',        icon:AlertTriangle },
  { key:'remesas',  label:'Remesas',          icon:Send },
];

export default function Cobros() {
  const { esAgenteRestringido } = useApp();
  const [tab, setTab] = useState('pagos');
  // Remesas es interno de RU: no se muestra a agentes restringidos
  const tabsVisibles = esAgenteRestringido ? TABS.filter(t => t.key !== 'remesas') : TABS;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Cobros</div>
          <div className="page-subtitle">Gestión de pagos, estados de cuenta y morosidad</div>
        </div>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:24, background:'rgba(var(--navy-rgb),0.4)', borderRadius:10, padding:4, width:'fit-content' }}>
        {tabsVisibles.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={()=>setTab(t.key)} style={{
              display:'flex', alignItems:'center', gap:8, padding:'8px 16px',
              borderRadius:8, border:'none', cursor:'pointer', fontFamily:'var(--font)',
              fontSize:13, fontWeight:600,
              background: tab===t.key ? 'var(--sky)' : 'transparent',
              color: tab===t.key ? '#fff' : 'var(--silver)',
            }}>
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'pagos'   && <TabPagos />}
      {tab === 'estado'  && <TabEstadoCuenta />}
      {tab === 'moroso'  && <TabMorosidad />}
      {tab === 'remesas' && <TabRemesas />}
    </div>
  );
}
